import {
  useAddConsumer,
  useConsumerDetail,
  useUpdateConsumer,
} from "@features/cx/consumerOnboarding/hooks";
import { toast } from "@shared/hooks/use-toast";
import { FormService } from "@shared/services/FormServices";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Button } from "@shared/ui/button";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { AlertCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConsumerDetail } from "./consumerDetail";
import { ServiceDetail } from "./serviceDetail";
import { DocumentUploadComponent } from "./uploadDocuments";

interface ApplicationDetailLandingProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  data?: any;
  onDataChange?: (data: any) => void;
}

export function ApplicationDetailLanding({
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  data,
  onDataChange,
}: ApplicationDetailLandingProps) {
  const navigate = useNavigate();

  // Use refs to store data to prevent recreation on every render
  const consumerDataRef = useRef<any>(null);
  const serviceDataRef = useRef<any>(null);
  const documentDataRef = useRef<any>(null);

  const consumerFormRef = useRef<any>(null);
  const serviceFormRef = useRef<any>(null);

  const [isConsumerDataValid, setIsConsumerDataValid] = useState(false);
  const [isServiceDataValid, setIsServiceDataValid] = useState(false);
  const [isDocumentDataValid, setIsDocumentDataValid] = useState(false);

  const remoteUtilityId = getRemoteUtilityId();

  const { consumerId } = useParams();
  const { data: consumerDetailData, refetch: refetchConsumerDetail } =
    useConsumerDetail(consumerId);

  // Initialize the mutation hooks
  const { mutate: addConsumer, isPending: isAddingConsumer } = useAddConsumer();
  const { mutate: updateConsumer, isPending: isUpdatingConsumer } =
    useUpdateConsumer();

  const isSubmitting = isAddingConsumer || isUpdatingConsumer;
  useEffect(() => {
    if (consumerId) {
      refetchConsumerDetail();
    }
  }, [consumerId, refetchConsumerDetail]);

  const handleConsumerFormReady = useCallback((form: any) => {
    consumerFormRef.current = form;
  }, []);

  // Add callback to receive service form (if ServiceDetail also has a form)
  const handleServiceFormReady = useCallback((form: any) => {
    serviceFormRef.current = form;
  }, []);
  // Stable handlers using useCallback with refs
  const handleConsumerDataChange = useCallback(
    (newData: any) => {
      consumerDataRef.current = newData;

      const combined = {
        consumer: newData,
        service: serviceDataRef.current,
        document: documentDataRef.current,
      };
      try {
        localStorage.setItem("consumerFirstStepData", JSON.stringify(combined));
      } catch (e) {}

      // Only notify parent if onDataChange exists
      if (onDataChange) {
        onDataChange(combined);
      }
    },
    [onDataChange],
  );

  const handleServiceDataChange = useCallback(
    (newData: any) => {
      serviceDataRef.current = newData;

      const combined = {
        consumer: consumerDataRef.current,
        service: newData,
        document: documentDataRef.current,
      };
      try {
        localStorage.setItem("consumerFirstStepData", JSON.stringify(combined));
      } catch (e) {}

      // Only notify parent if onDataChange exists
      if (onDataChange) {
        onDataChange(combined);
      }
    },
    [onDataChange],
  );

  const handleDocumentDataChange = useCallback(
    (newData: any) => {
      documentDataRef.current = newData;

      const combined = {
        consumer: consumerDataRef.current,
        service: serviceDataRef.current,
        document: newData,
      };
      try {
        localStorage.setItem("consumerFirstStepData", JSON.stringify(combined));
      } catch (e) {}

      // Only notify parent if onDataChange exists
      if (onDataChange) {
        onDataChange(combined);
      }
    },
    [onDataChange],
  );

  // Stable validation handler
  const handleDocumentValidationChange = useCallback((isValid: boolean) => {
    setIsDocumentDataValid(isValid);
  }, []);

  // Helper function to build the API payload with FormData
  const buildFormDataPayload = useCallback(() => {
    const formData = new FormData();
    const consumerData = consumerDataRef.current;
    const serviceData = serviceDataRef.current;
    const documentData = documentDataRef.current;

    // Add remote_utility_id
    formData.append("remote_utility_id", remoteUtilityId?.toString());

    // Build consumer details JSON
    const consumerDetails: any = {
      primary_consumer: {
        first_name: consumerData?.first_name || "",
        last_name: consumerData?.last_name || "",
        email: consumerData?.email || "",
        is_vip: consumerData?.vip_designation || false,
        contact_number: consumerData?.phone_number || "",
        category: consumerData?.account_category || "",
        category_display: consumerData?.account_category_display || "",
        sub_category: consumerData?.account_sub_category || "",
        ...(consumerData?.ssn ? { ssn_encrypted: consumerData.ssn } : {}),
        ...(consumerData?.date_of_birth
          ? { date_of_birth: consumerData.date_of_birth }
          : {}),
        sub_category_display: consumerData?.account_sub_category_display || "",
        registration_date: serviceData?.service_start_date || "",
        preferred_contact_method: serviceData?.preferred_contact_methods || [],
        utility_service:
          serviceData?.utility_service ||
          serviceData?.service_quantities ||
          serviceData?.number_of_connections ||
          consumerDetailData?.result?.primary_consumer?.utility_service ||
          {},
      },
    };

    // Add secondary consumer if co-applicant is added
    if (consumerData?.add_co_applicant && consumerData?.co_applicant) {
      consumerDetails.secondary_consumer = {
        first_name: consumerData.co_applicant.first_name || "",
        last_name: consumerData.co_applicant.last_name || "",
        email: consumerData.co_applicant.email || "",
        contact_number: consumerData.co_applicant.phone_number || "",
        secondary_relationship: consumerData.co_applicant.relationship || "",
        secondary_consumer_display:
          consumerData.co_applicant.relationship_display || "",
        ...(consumerData.co_applicant.date_of_birth
          ? { date_of_birth: consumerData.co_applicant.date_of_birth }
          : {}),
      };
    } else {
      if (!consumerData?.add_co_applicant) {
        consumerDetails.secondary_consumer = {};
      }
    }

    formData.append("consumer_details", JSON.stringify(consumerDetails));

    // Add source
    formData.append("source", "0");

    // Build service address data JSON
    const serviceAddressData = {
      unit: serviceData?.service_address?.house_unit_number || "",
      address: serviceData?.service_address?.street_address || "",
      city: serviceData?.service_address?.city || "",
      zipcode: serviceData?.service_address?.zip_postal_code || "",
    };
    formData.append("service_address_data", JSON.stringify(serviceAddressData));

    // Build billing address data JSON
    let billingAddressData;
    if (serviceData?.same_as_service_address === true) {
      // If "Same as service address" is checked, copy service address to billing
      billingAddressData = {
        unit: serviceData.service_address?.house_unit_number || "",
        address: serviceData.service_address?.street_address || "",
        city: serviceData.service_address?.city || "",
        zipcode: serviceData.service_address?.zip_postal_code || "",
      };
    } else {
      // If different, use mailing address data
      billingAddressData = {
        unit: serviceData?.mailing_address?.house_unit_number || "",
        address: serviceData?.mailing_address?.street_address || "",
        city: serviceData?.mailing_address?.city || "",
        zipcode: serviceData?.mailing_address?.zip_postal_code || "",
      };
    }
    formData.append("billing_address_data", JSON.stringify(billingAddressData));
    formData.append(
      "is_e_bill",
      serviceData?.paperless_billing ? "True" : "False",
    );
    const propertyType = serviceData?.property_type || "";

    if (propertyType === "individual") {
      formData.append(
        "ownership_details",
        JSON.stringify({
          structure_choice: "INDIVIDUAL",
        }),
      );
    } else if (propertyType === "tenant") {
      formData.append(
        "ownership_details",
        JSON.stringify({
          structure_choice: "Tenant",
          ownership_detail: {
            owner_name: serviceData?.ownerDetails?.name || "",
            owner_phone: serviceData?.ownerDetails?.phone || "",
            owner_email: serviceData?.ownerDetails?.email || null,
            owner_type: serviceData?.ownerDetails?.type || "",
            owner_address: serviceData?.ownerDetails?.address || "",
          },
        }),
      );
    } else if (propertyType === "business") {
      const businessLink = serviceData?.business_account_link || "";

      if (businessLink === "new") {
        formData.append(
          "ownership_details",
          JSON.stringify({
            structure_choice: "Master",
          }),
        );
      } else if (businessLink === "existing") {
        formData.append(
          "ownership_details",
          JSON.stringify({
            structure_choice: "Sub_account",
            master_account_no:
              serviceData?.existing_business_account?.account_number || "",
            master_account_name:
              serviceData?.existing_business_account?.account_email || "",
            master_account_id:
              serviceData?.existing_business_account?.account_id || "",
          }),
        );
      }
    }

    // Add document files and build document JSON
    if (documentData?.newFiles && documentData.newFiles.length > 0) {
      const primaryConsumerKyc: any[] = [];

      documentData.newFiles.forEach((docFile: any, index: number) => {
        const fileKey = `file${index + 1}`;
        formData.append(fileKey, docFile.file);

        primaryConsumerKyc.push({
          id: null,
          document_type: docFile.documentType,
          document_subtype: docFile.documentSubtype,
          reference_id: docFile.referenceId,
          file_key: fileKey,
          consumer_role: "Primary",
        });
      });

      // Add existing documents (for edit mode)
      if (
        documentData?.existingDocuments &&
        documentData.existingDocuments.length > 0
      ) {
        documentData.existingDocuments.forEach((existingDoc: any) => {
          primaryConsumerKyc.push({
            id: existingDoc.id,
            document_type: existingDoc.documentType,
            document_subtype: existingDoc.documentSubtype,
            reference_id: existingDoc.referenceId,
            file_key: null,
            consumer_role: "Primary",
          });
        });
      }

      const documentPayload = { primary_consumer_kyc: primaryConsumerKyc };
      formData.append("document", JSON.stringify(documentPayload));
    }

    return formData;
  }, [remoteUtilityId]);

  const handleContinue = useCallback(async () => {
    let isValid = true;
    let errorMessages = [];

    if (consumerFormRef.current) {
      const isConsumerValid = await FormService.validateForm(
        consumerFormRef.current,
      );
      if (!isConsumerValid) {
        isValid = false;
        errorMessages.push("consumer details");
      }
    }

    if (serviceFormRef.current) {
      const isServiceValid = await FormService.validateForm(
        serviceFormRef.current,
      );
      if (!isServiceValid) {
        isValid = false;
        errorMessages.push("service details");
      }
    }

    if (!isDocumentDataValid) {
      isValid = false;
      errorMessages.push("required documents");
    }

    if (!isValid) {
      toast({
        title: "Validation Error",
        description: `Please complete all required fields for: ${errorMessages.join(", ")}.`,
        variant: "destructive",
      });
      return;
    }

    const combined = {
      consumer: consumerDataRef.current,
      service: serviceDataRef.current,
      document: documentDataRef.current,
    };
    try {
      localStorage.setItem("consumerFirstStepData", JSON.stringify(combined));
    } catch (e) {}

    if (onDataChange) {
      onDataChange(combined);
    }

    // Build the FormData payload
    const formDataPayload = buildFormDataPayload();

    // Determine if we're updating or creating
    if (consumerId) {
      // Update existing consumer
      updateConsumer(
        { consumerId, payload: formDataPayload },
        {
          onSuccess: (response) => {
            const consumerInfo = response?.result;
            const categoryCode = consumerInfo?.category;
            toast({
              title: "Success",
              description: "Consumer information updated successfully!",
              variant: "default",
            });

            // Move to next step using stepper's onNext
            if (onNext) {
              onNext();
            }
          },
          onError: (error: any) => {
            toast({
              title: "Error",
              description:
                error?.response?.data?.message ||
                "Failed to update consumer. Please try again.",
              variant: "destructive",
            });
          },
        },
      );
    } else {
      // Create new consumer
      addConsumer(formDataPayload, {
        onSuccess: (response) => {
          const consumerInfo = response?.result;
          const categoryCode = consumerInfo?.category;
          const newConsumerId = consumerInfo?.id;
          if (newConsumerId) {
            navigate(
              `/cx/consumer-management/manual/edit/${newConsumerId}#application-review`,
            );
          }

          toast({
            title: "Success",
            description: "Consumer Details Added successfully!",
            variant: "default",
          });

          // Move to next step using stepper's onNext
          if (onNext) {
            onNext({ categoryCode });
          }
        },
      });
    }
  }, [
    isConsumerDataValid,
    isServiceDataValid,
    isDocumentDataValid,
    buildFormDataPayload,
    consumerId,
    updateConsumer,
    addConsumer,
    navigate,
    onNext,
  ]);

  const handleCancel = useCallback(() => {
    if (consumerId) {
      refetchConsumerDetail();
    }
    // Navigate back or to previous step
    if (onPrevious && !isFirstStep) {
      onPrevious();
    } else {
      window.history.back();
    }
  }, [onPrevious, isFirstStep, consumerId, refetchConsumerDetail]);

  return (
    <div className="w-full space-y-6">
      {/* Important Information Accordion */}
      <Accordion type="single" collapsible>
        <AccordionItem
          value="Important Information"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-3" />
              <span className="font-medium text-blue-800">
                Important Information
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="text-blue-700 text-sm space-y-1 list-disc pl-5">
              <li>All fields marked with an asterisk (*) are required</li>
              <li>The primary consumer will be the main account holder</li>
              <li>Email verification is required before proceeding</li>
              <li>VIP designation requires approval from management</li>
              <li>You can save your progress as a draft at any time</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Consumer Detail Form */}
      <div className="bg-white rounded-lg border border-gray-200 mt-4 p-4">
        <ConsumerDetail
          onDataChange={handleConsumerDataChange}
          onValidationChange={setIsConsumerDataValid}
          consumerData={consumerDetailData}
          onFormReady={handleConsumerFormReady}
        />
      </div>

      {/* Service Information Accordion */}
      <Accordion type="single" collapsible>
        <AccordionItem
          value="service-info"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-3" />
              <span className="font-medium text-blue-800">
                Service Information
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="text-blue-700 text-sm space-y-1 list-disc pl-5">
              <li>
                Service Address: Enter the accurate service address as this will
                be the location where we provide you services. Ensure all
                details are correct for proper service delivery.
              </li>
              <li>
                Service Selection: Carefully select the services that you want.
                Review all available options and choose only the services you
                need.
              </li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Service Detail Form */}
      <div className="bg-white rounded-lg border border-gray-200 mt-4 p-4">
        <ServiceDetail
          onDataChange={handleServiceDataChange}
          onValidationChange={setIsServiceDataValid}
          consumerData={consumerDetailData}
          onFormReady={handleServiceFormReady}
        />
      </div>

      {/* Document Upload Section */}
      <div className="bg-white rounded-lg border border-gray-200 mt-4 p-4">
        <DocumentUploadComponent
          remoteUtilityId={remoteUtilityId}
          onDataChange={handleDocumentDataChange}
          onValidationChange={handleDocumentValidationChange}
          consumerData={consumerDetailData}
        />
      </div>

      {/* Navigation Buttons - ALWAYS VISIBLE */}
      <div className="flex justify-end pt-6 gap-3 border-t border-gray-200 bg-white sticky bottom-0 z-10">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {isFirstStep ? "Cancel" : "Back"}
        </Button>

        <Button
          variant="default"
          className="bg-blue-600 hover:bg-blue-700"
          onClick={handleContinue}
        >
          {isSubmitting ? (
            <>Submitting...</>
          ) : isLastStep ? (
            "Submit Application"
          ) : consumerId ? (
            "Save & Review"
          ) : (
            "Continue to Review"
          )}
        </Button>
      </div>
    </div>
  );
}
